import { useState, useEffect, useMemo } from 'react';
import {
  Plus,
  Save,
  X,
  Palette,
  GraduationCap,
  AlertCircle,
  ChevronDown,
  Zap,
  Sparkles,
} from 'lucide-react';
import { useMappingStore } from '../../../stores/mappingStore';
import type {
  SourceModule,
  MappableProperty,
  EnhancedFieldMapping,
  DiscoveredVirtualField,
} from '../../../types/mapping';
import { SOURCE_MODULES, MAPPABLE_PROPERTIES } from '../../../types/mapping';

interface MappingFormProps {
  elementId: string;
  elementType: 'text' | 'image' | 'shape' | 'button' | 'path' | 'svg' | 'line';
  editingMapping: EnhancedFieldMapping | null;
  preSelectedSource: SourceModule | null;
  preSelectedTable: string | null;
  preSelectedColumn: string | null;
  preSelectedDataType: string | null;
  preSelectedVirtualField?: DiscoveredVirtualField | null;
  onClose: () => void;
  onSaved: () => void;
  multiElementIds?: string[];
  multiValidProperties?: string[];
}

function groupByCategory(
  properties: typeof MAPPABLE_PROPERTIES,
  elementType: string
) {
  const filtered = properties.filter((p) =>
    p.supportedElementTypes.includes(elementType as 'text' | 'image' | 'shape' | 'button')
  );
  const groups: Record<string, typeof MAPPABLE_PROPERTIES> = {};
  for (const prop of filtered) {
    if (!groups[prop.category]) groups[prop.category] = [];
    groups[prop.category].push(prop);
  }
  return groups;
}

const CATEGORY_LABELS: Record<string, string> = {
  content: 'Content',
  typography: 'Typography',
  color: 'Colors',
  media: 'Media',
  interaction: 'Interaction',
  display: 'Display',
};

const TRANSFORM_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'uppercase', label: 'UPPERCASE' },
  { value: 'lowercase', label: 'lowercase' },
  { value: 'capitalize', label: 'Capitalize' },
  { value: 'prefix:', label: 'Prefix...' },
  { value: 'suffix:', label: 'Suffix...' },
  { value: 'truncate:', label: 'Truncate...' },
];

export default function MappingForm({
  elementId,
  elementType,
  editingMapping,
  preSelectedSource,
  preSelectedTable,
  preSelectedColumn,
  preSelectedDataType,
  preSelectedVirtualField,
  onClose,
  onSaved,
  multiElementIds,
  multiValidProperties,
}: MappingFormProps) {
  const { schema, addMapping, updateMapping, addMappingToElements, elementMappings } = useMappingStore();

  const isEditing = !!editingMapping;

  const [source, setSource] = useState<SourceModule | null>(
    editingMapping?.source ?? preSelectedSource ?? null
  );
  const [tableName, setTableName] = useState<string>(
    editingMapping?.tableName ?? preSelectedTable ?? ''
  );
  const [columnName, setColumnName] = useState<string>(
    editingMapping?.columnName ?? preSelectedColumn ?? ''
  );
  const [columnDataType, setColumnDataType] = useState<string>(
    editingMapping?.columnDataType ?? preSelectedDataType ?? ''
  );
  const [virtualField, setVirtualField] = useState<DiscoveredVirtualField | null>(
    preSelectedVirtualField ?? null
  );
  const [targetProperty, setTargetProperty] = useState<MappableProperty | ''>(
    editingMapping?.targetProperty ?? ''
  );
  const [fallbackValue, setFallbackValue] = useState<string>(
    editingMapping?.fallbackValue ?? ''
  );
  const [transformBase, setTransformBase] = useState<string>('');
  const [transformArg, setTransformArg] = useState<string>('');

  useEffect(() => {
    if (editingMapping?.transformExpression) {
      const expr = editingMapping.transformExpression;
      if (expr.startsWith('prefix:')) {
        setTransformBase('prefix:');
        setTransformArg(expr.slice(7));
      } else if (expr.startsWith('suffix:')) {
        setTransformBase('suffix:');
        setTransformArg(expr.slice(7));
      } else if (expr.startsWith('truncate:')) {
        setTransformBase('truncate:');
        setTransformArg(expr.slice(9));
      } else {
        setTransformBase(expr);
        setTransformArg('');
      }
    }
  }, [editingMapping]);

  useEffect(() => {
    if (preSelectedSource && !isEditing) setSource(preSelectedSource);
  }, [preSelectedSource, isEditing]);

  useEffect(() => {
    if (preSelectedTable && !isEditing) setTableName(preSelectedTable);
  }, [preSelectedTable, isEditing]);

  useEffect(() => {
    if (preSelectedColumn && !isEditing) setColumnName(preSelectedColumn);
  }, [preSelectedColumn, isEditing]);

  useEffect(() => {
    if (preSelectedDataType && !isEditing) setColumnDataType(preSelectedDataType);
  }, [preSelectedDataType, isEditing]);

  useEffect(() => {
    if (preSelectedVirtualField && !isEditing) setVirtualField(preSelectedVirtualField);
  }, [preSelectedVirtualField, isEditing]);

  useEffect(() => {
    if (!isEditing || !editingMapping || !schema) return;
    if (
      editingMapping.filterColumn === 'attribute_key' &&
      editingMapping.filterValue &&
      editingMapping.columnName === 'attribute_value'
    ) {
      const table = schema.tables.find(
        (t) => t.table_name === editingMapping.tableName
      );
      const vf = table?.virtual_columns?.find(
        (v) => v.field_key === editingMapping.filterValue
      );
      if (vf) setVirtualField(vf);
    }
  }, [isEditing, editingMapping, schema]);

  const availableTables = useMemo(() => {
    if (!schema || !source) return [];
    return schema.tables.filter((t) => t.source === source);
  }, [schema, source]);

  const availableColumns = useMemo(() => {
    if (!schema || !tableName) return [];
    const table = schema.tables.find((t) => t.table_name === tableName);
    return table?.columns ?? [];
  }, [schema, tableName]);

  const availableVirtualFields = useMemo(() => {
    if (!schema || !tableName) return [];
    const table = schema.tables.find((t) => t.table_name === tableName);
    return table?.virtual_columns ?? [];
  }, [schema, tableName]);

  const virtualFieldsByCategory = useMemo(() => {
    const groups: Record<string, DiscoveredVirtualField[]> = {};
    for (const vf of availableVirtualFields) {
      (groups[vf.category] = groups[vf.category] ?? []).push(vf);
    }
    return groups;
  }, [availableVirtualFields]);

  const propertyGroups = useMemo(
    () => groupByCategory(MAPPABLE_PROPERTIES, elementType),
    [elementType]
  );

  const existingMappings = elementMappings[elementId] ?? [];
  const usedTargetProperties = new Set(
    existingMappings
      .filter((m) => m.id !== editingMapping?.id)
      .map((m) => m.targetProperty)
  );

  const selectedPropertyConfig = MAPPABLE_PROPERTIES.find(
    (p) => p.key === targetProperty
  );
  const isTypeCompatible =
    !selectedPropertyConfig ||
    !columnDataType ||
    selectedPropertyConfig.compatibleDataTypes.some((dt) =>
      columnDataType.toLowerCase().includes(dt.toLowerCase())
    );

  const transformExpression = transformBase
    ? transformBase.endsWith(':')
      ? `${transformBase}${transformArg}`
      : transformBase
    : null;

  const canSave =
    source &&
    tableName &&
    columnName &&
    columnDataType &&
    targetProperty &&
    (!transformBase.endsWith(':') || transformArg.trim().length > 0);

  const handleSourceChange = (newSource: SourceModule) => {
    setSource(newSource);
    setTableName('');
    setColumnName('');
    setColumnDataType('');
    setVirtualField(null);
  };

  const handleTableChange = (newTable: string) => {
    setTableName(newTable);
    setColumnName('');
    setColumnDataType('');
    setVirtualField(null);
  };

  const handleColumnChange = (newValue: string) => {
    if (newValue.startsWith('virtual:')) {
      const key = newValue.slice(8);
      const vf = availableVirtualFields.find((v) => v.field_key === key);
      if (vf) {
        setVirtualField(vf);
        setColumnName('attribute_value');
        setColumnDataType(vf.data_type);
      }
      return;
    }
    setVirtualField(null);
    setColumnName(newValue);
    const col = availableColumns.find((c) => c.column_name === newValue);
    if (col) setColumnDataType(col.data_type);
  };

  const handleSave = async () => {
    if (!canSave || !source || !targetProperty) return;

    const mappingPayload = {
      source,
      tableName,
      columnName,
      columnDataType,
      targetProperty: targetProperty as MappableProperty,
      fallbackValue,
      transformExpression,
      filterColumn: virtualField ? 'attribute_key' : null,
      filterValue: virtualField ? virtualField.field_key : null,
      filterCategory: virtualField ? virtualField.category : null,
    };

    if (Array.isArray(multiElementIds) && multiElementIds.length > 1) {
      await addMappingToElements(multiElementIds, mappingPayload);
      onSaved();
      return;
    }

    if (isEditing && editingMapping) {
      await updateMapping(elementId, editingMapping.id, mappingPayload);
    } else {
      await addMapping(elementId, mappingPayload);
    }

    onSaved();
  };

  return (
    <div className="bg-[#12121a] border border-white/[0.08] rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 bg-[#1a1a2e]/50 border-b border-white/[0.06]">
        <div className="flex items-center gap-2">
          <Zap className="w-3.5 h-3.5 text-purple-400" />
          <span className="text-xs font-semibold text-white/80">
            {isEditing ? 'Edit Mapping' : 'New Mapping'}
          </span>
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-white/[0.06] text-white/30 hover:text-white/50 transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="p-3 space-y-3">
        <div>
          <label className="block text-[10px] text-white/40 uppercase tracking-wider mb-1.5">
            Data Source
          </label>
          <div className="flex gap-2">
            {(Object.keys(SOURCE_MODULES) as SourceModule[]).map((key) => {
              const config = SOURCE_MODULES[key];
              const isActive = source === key;
              const Icon = key === 'ad' ? Palette : GraduationCap;
              return (
                <button
                  key={key}
                  onClick={() => handleSourceChange(key)}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md border text-xs font-medium transition-colors ${
                    isActive
                      ? key === 'ad'
                        ? 'bg-pink-500/15 border-pink-500/30 text-pink-300'
                        : 'bg-blue-500/15 border-blue-500/30 text-blue-300'
                      : 'bg-white/[0.02] border-white/[0.06] text-white/40 hover:text-white/60 hover:border-white/[0.1]'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {config.label}
                </button>
              );
            })}
          </div>
        </div>

        {source && (
          <div>
            <label className="block text-[10px] text-white/40 uppercase tracking-wider mb-1.5">
              Table
            </label>
            <div className="relative">
              <select
                value={tableName}
                onChange={(e) => handleTableChange(e.target.value)}
                className="w-full px-3 py-1.5 text-xs bg-white/[0.04] border border-white/[0.06] rounded-md text-white appearance-none focus:outline-none focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/20 transition-colors"
              >
                <option value="" className="bg-[#1a1a2e]">
                  Select a table...
                </option>
                {availableTables.map((table) => (
                  <option
                    key={table.table_name}
                    value={table.table_name}
                    className="bg-[#1a1a2e]"
                  >
                    {table.table_name} ({table.columns.length} columns)
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30 pointer-events-none" />
            </div>
          </div>
        )}

        {tableName && (
          <div>
            <label className="block text-[10px] text-white/40 uppercase tracking-wider mb-1.5">
              Column
            </label>
            <div className="relative">
              <select
                value={virtualField ? `virtual:${virtualField.field_key}` : columnName}
                onChange={(e) => handleColumnChange(e.target.value)}
                className="w-full px-3 py-1.5 text-xs bg-white/[0.04] border border-white/[0.06] rounded-md text-white appearance-none focus:outline-none focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/20 transition-colors"
              >
                <option value="" className="bg-[#1a1a2e]">
                  Select a column...
                </option>
                {Object.entries(virtualFieldsByCategory).map(([category, fields]) => (
                  <optgroup
                    key={category}
                    label={`Virtual - ${category}`}
                    className="bg-[#1a1a2e]"
                  >
                    {fields.map((vf) => (
                      <option
                        key={vf.field_key}
                        value={`virtual:${vf.field_key}`}
                        className="bg-[#1a1a2e]"
                      >
                        {vf.label} ({vf.data_type})
                      </option>
                    ))}
                  </optgroup>
                ))}
                {availableColumns.length > 0 && (
                  <optgroup label="Columns" className="bg-[#1a1a2e]">
                    {availableColumns.map((col) => (
                      <option
                        key={col.column_name}
                        value={col.column_name}
                        className="bg-[#1a1a2e]"
                      >
                        {col.column_name} ({col.data_type})
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30 pointer-events-none" />
            </div>
            {virtualField && (
              <div className="flex items-center gap-1.5 mt-1.5 px-1">
                <Sparkles className="w-3 h-3 text-cyan-400" />
                <span className="text-[10px] text-cyan-400/80">
                  Virtual field: {virtualField.label} &middot; {virtualField.category}
                </span>
              </div>
            )}
          </div>
        )}

        {columnName && (
          <div>
            <label className="block text-[10px] text-white/40 uppercase tracking-wider mb-1.5">
              Map To Element Property
            </label>
            <div className="space-y-1.5">
              {Object.entries(propertyGroups).map(([category, props]) => {
                const filteredProps = Array.isArray(multiValidProperties)
                  ? props.filter((p) => multiValidProperties.includes(p.key))
                  : props;
                if (filteredProps.length === 0) return null;
                return (
                <div key={category}>
                  <span className="text-[10px] text-white/25 uppercase tracking-wider">
                    {CATEGORY_LABELS[category] || category}
                  </span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {filteredProps.map((prop) => {
                      const isUsed = usedTargetProperties.has(prop.key);
                      const isActive = targetProperty === prop.key;
                      const isCompatible =
                        !columnDataType ||
                        prop.compatibleDataTypes.some((dt) =>
                          columnDataType.toLowerCase().includes(dt.toLowerCase())
                        );

                      return (
                        <button
                          key={prop.key}
                          onClick={() => setTargetProperty(prop.key)}
                          disabled={isUsed}
                          title={
                            isUsed
                              ? 'Already mapped by another mapping'
                              : !isCompatible
                              ? `Type mismatch: ${columnDataType} -> ${prop.key}`
                              : prop.description
                          }
                          className={`px-2 py-1 rounded text-[10px] font-medium border transition-colors ${
                            isUsed
                              ? 'bg-white/[0.02] border-white/[0.04] text-white/20 cursor-not-allowed line-through'
                              : isActive
                              ? 'bg-purple-500/15 border-purple-500/30 text-purple-300'
                              : !isCompatible
                              ? 'bg-amber-500/5 border-amber-500/15 text-amber-400/60 hover:border-amber-500/30'
                              : 'bg-white/[0.02] border-white/[0.06] text-white/50 hover:text-white/70 hover:border-white/[0.1]'
                          }`}
                        >
                          {prop.label}
                          {!isCompatible && !isUsed && (
                            <AlertCircle className="w-2.5 h-2.5 inline ml-1 -mt-0.5" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        )}

        {targetProperty && !isTypeCompatible && (
          <div className="flex items-start gap-2 p-2 rounded-md bg-amber-500/10 border border-amber-500/20">
            <AlertCircle className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />
            <div>
              <p className="text-[10px] text-amber-400 font-medium">Type Mismatch</p>
              <p className="text-[10px] text-amber-400/70 mt-0.5">
                Column type <span className="font-mono">{columnDataType}</span> may not be
                compatible with <span className="font-mono">{targetProperty}</span>. A
                fallback value is recommended.
              </p>
            </div>
          </div>
        )}

        {targetProperty && (
          <div>
            <label className="block text-[10px] text-white/40 uppercase tracking-wider mb-1.5">
              Fallback Value
              <span className="text-white/20 normal-case tracking-normal ml-1">
                (used when data is missing)
              </span>
            </label>
            <input
              type="text"
              value={fallbackValue}
              onChange={(e) => setFallbackValue(e.target.value)}
              placeholder="Default value if no data..."
              className="w-full px-3 py-1.5 text-xs bg-white/[0.04] border border-white/[0.06] rounded-md text-white placeholder-white/20 focus:outline-none focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/20 transition-colors"
            />
          </div>
        )}

        {targetProperty && (
          <div>
            <label className="block text-[10px] text-white/40 uppercase tracking-wider mb-1.5">
              Transform
              <span className="text-white/20 normal-case tracking-normal ml-1">(optional)</span>
            </label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <select
                  value={transformBase}
                  onChange={(e) => {
                    setTransformBase(e.target.value);
                    if (!e.target.value.endsWith(':')) setTransformArg('');
                  }}
                  className="w-full px-3 py-1.5 text-xs bg-white/[0.04] border border-white/[0.06] rounded-md text-white appearance-none focus:outline-none focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/20 transition-colors"
                >
                  {TRANSFORM_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value} className="bg-[#1a1a2e]">
                      {opt.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30 pointer-events-none" />
              </div>
              {transformBase.endsWith(':') && (
                <input
                  type="text"
                  value={transformArg}
                  onChange={(e) => setTransformArg(e.target.value)}
                  placeholder={
                    transformBase === 'prefix:'
                      ? 'e.g., https://'
                      : transformBase === 'suffix:'
                      ? 'e.g., .edu'
                      : 'e.g., 50'
                  }
                  className="flex-1 px-3 py-1.5 text-xs bg-white/[0.04] border border-white/[0.06] rounded-md text-white placeholder-white/20 focus:outline-none focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/20 transition-colors"
                />
              )}
            </div>
          </div>
        )}

        <div className="flex gap-2 pt-1">
          <button
            onClick={onClose}
            className="flex-1 px-3 py-1.5 text-xs text-white/50 bg-white/[0.04] border border-white/[0.06] rounded-md hover:bg-white/[0.06] hover:text-white/70 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-purple-500/20 border border-purple-500/30 text-purple-300 hover:bg-purple-500/30"
          >
            {isEditing ? (
              <>
                <Save className="w-3 h-3" />
                Update
              </>
            ) : (
              <>
                <Plus className="w-3 h-3" />
                Add Mapping
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
